import { GameScene } from '../GameScene';
import {
  getFactionLabel,
  getFactionColor,
  getRankByIndex,
  getNextRank,
  type FactionId,
} from '@shared/data/factions';
import { GAME_FONT_FAMILY } from '@shared/constants';
import {
  ClientMessageType,
  type FactionStatsMessage,
  type FactionJoinPromptMessage,
  type FactionRankInfoMessage,
} from '@shared/types/messages';

/**
 * UI de facciones del cliente: recibe stats/prompts/rank del server y arma el overlay de facción
 * (unirse / ver rango / banner). Extraído de GameScene (god-object). El estado (localFaction/Rank/
 * Honor/EnemiesKilled, factionOverlay) vive en GameScene (host) y se accede vía `this.scene.*`.
 */
export class FactionUI {
  constructor(private scene: GameScene) {}

  public handleFactionStats(m: FactionStatsMessage): void {
    // Burbuja "+N Honor" si el honor subió (kill de enemigo de la otra ciudad).
    const honorDelta = m.honor - this.scene.localHonor;
    if (honorDelta > 0 && this.scene.localFaction !== '' && this.scene.localSessionId) {
      this.scene.overheadText.showChatBubble(this.scene.localSessionId, `+${honorDelta} Honor`, '#ffd700');
    }
    this.scene.localFaction = m.faction;
    this.scene.localFactionRank = m.factionRank;
    this.scene.localHonor = m.honor;
    this.scene.localEnemiesKilled = m.enemiesKilled;
  }

  /** El Reclutador ofrece unirse: mostrar confirmación. Al aceptar, JOIN_FACTION. */
  public handleFactionJoinPrompt(m: FactionJoinPromptMessage): void {
    this.closeFactionOverlay();
    const overlay = this.buildFactionOverlay();
    const color = getFactionColor(m.factionId as FactionId);
    overlay.innerHTML = `
      <div style="font-size:18px;font-weight:bold;color:${color};margin-bottom:8px">Unirse a ${m.factionLabel}</div>
      <div style="margin-bottom:14px;max-width:320px;line-height:1.4">
        ¿Querés jurar lealtad a <b style="color:${color}">${m.factionLabel}</b>?
        Esta decisión define tu bando en la guerra. Para abandonarla deberás completar una misión.
      </div>`;
    const join = this.makeOverlayButton('Unirme', color);
    const cancel = this.makeOverlayButton('Cancelar', '#666');
    join.onclick = (): void => {
      this.scene.colyseusClient.send({ type: ClientMessageType.JOIN_FACTION, factionId: m.factionId } as never);
      this.closeFactionOverlay();
    };
    cancel.onclick = (): void => this.closeFactionOverlay();
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:10px;justify-content:center;margin-top:8px';
    row.append(join, cancel);
    overlay.appendChild(row);
  }

  /** Info de rango del Rey: panel con rango/kills/ascenso. */
  public handleFactionRankInfo(m: FactionRankInfoMessage): void {
    this.closeFactionOverlay();
    const overlay = this.buildFactionOverlay();
    const color = getFactionColor(m.faction as FactionId);
    const rankLabel = getRankByIndex(m.factionRank).labelEs;
    const next = getNextRank(m.factionRank);
    const progress = next
      ? `${m.enemiesKilled} / ${m.nextThreshold} enemigos para <b>${next.labelEs}</b>`
      : 'Alcanzaste el rango máximo: Héroe.';
    overlay.innerHTML = `
      <div style="font-size:18px;font-weight:bold;color:${color};margin-bottom:6px">Rey de ${getFactionLabel(m.faction as FactionId)}</div>
      <div style="margin-bottom:6px">Rango actual: <b style="color:${color}">${rankLabel}</b></div>
      <div style="margin-bottom:6px">Honor: ${m.honor} · Enemigos eliminados: ${m.enemiesKilled}</div>
      <div style="margin-bottom:14px">${progress}</div>`;
    const ascend = this.makeOverlayButton(
      m.canAscend ? `Ascender a ${next?.labelEs ?? ''}` : 'No podés ascender aún',
      m.canAscend ? color : '#555'
    );
    if (!m.canAscend) ascend.style.opacity = '0.5';
    ascend.onclick = (): void => {
      if (!m.canAscend) return;
      this.scene.colyseusClient.send({ type: ClientMessageType.ASCEND_RANK, npcId: m.npcId } as never);
      this.closeFactionOverlay();
    };
    const close = this.makeOverlayButton('Cerrar', '#666');
    close.onclick = (): void => this.closeFactionOverlay();
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:10px;justify-content:center;margin-top:8px';
    row.append(ascend, close);
    overlay.appendChild(row);
  }

  /** Crea (o reusa) el contenedor DOM centrado del overlay de facción. */
  public buildFactionOverlay(): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.style.cssText =
      'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:10000;' +
      'background:rgba(20,20,28,0.96);border:2px solid #444;border-radius:8px;padding:20px 24px;' +
      'color:#eee;font-family:sans-serif;font-size:14px;text-align:center;box-shadow:0 8px 40px rgba(0,0,0,0.6)';
    document.body.appendChild(overlay);
    this.scene.factionOverlay = overlay;
    return overlay;
  }

  public makeOverlayButton(label: string, color: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = label;
    btn.style.cssText =
      `padding:8px 16px;border:none;border-radius:6px;background:${color};color:#fff;` +
      'font-size:14px;font-weight:bold;cursor:pointer';
    return btn;
  }

  public closeFactionOverlay(): void {
    if (this.scene.factionOverlay) {
      this.scene.factionOverlay.remove();
      this.scene.factionOverlay = null;
    }
  }

}
